import Link from 'next/link';

import type { ElementType } from 'react';

const urlPrefixConstants = ['http', 'tel:', 'mailto:'] as const;
export const internalDomains = ['prismatic.io', 'www.prismatic.io'] as const;

/**
 * Checks if the given URL starts with any of the URL prefix constants.
 *
 * @param url - The URL to check.
 * @returns `true` if the URL starts with any of the URL prefix constants, `false` otherwise.
 */
export const hasPrefixConstant = (url: string) =>
  urlPrefixConstants.some(prefix => url.toLowerCase().startsWith(prefix));

/**
 * Checks if the given URL is an internal link, based on the `internalDomains` list.
 *
 * @param url - The URL to check.
 * @returns `true` if the URL is an internal link, `false` otherwise.
 */
export const isInternalLink = (url: URL) => internalDomains.some(domain => url.hostname === domain);

/**
 * Converts a URL string to a URL object.
 *
 * @param href - The URL string to convert.
 * @returns A URL object representing the input URL string.
 * @throws {Error} If the input URL string is invalid.
 */
export const getUrlObject = (href: Maybe<string>) => {
  if (!href) {
    return undefined;
  }

  // Engines disagree on a raw space in the host: Chrome accepts
  // "https:// example.com/" and percent-encodes it, while Node, Firefox and
  // Safari reject it. The server rendered such a link as a <span> and Chrome
  // re-rendered it as an <a>, a hydration mismatch (seen on
  // /guides/integration-security-guide/). A host can never contain whitespace,
  // so reject it the same way everywhere. Spaces in the path are left alone:
  // every engine encodes those identically. Surrounding whitespace is just
  // copy-paste noise and is trimmed.
  href = href.trim();

  if (/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*\s/i.test(href)) {
    return undefined;
  }

  (href.charAt(0) === '/' || href.charAt(0) === '#') && (href = `https://${internalDomains[0]}${href}`);

  try {
    return new URL(href);
  } catch {
    return undefined;
  }
};

/**
 * Parses a URL string and returns an object with information about the URL.
 *
 * @param href - The URL string to parse.
 * @returns An object with the following properties:
 *   - Component: The appropriate React component to use for the URL ('Link' for internal URLs, 'a' for external URLs).
 *   - rel: The 'rel' attribute for the link ('noreferrer noopener' for external URLs, empty string for internal URLs).
 *   - target: The 'target' attribute for the link ('_blank' for external URLs, empty string for internal URLs).
 *   - href: The URL path (without the domain) for internal URLs, or the full URL for external URLs.
 */
const parseUrl = (href: Maybe<string>, fallback?: ElementType) => {
  const url = getUrlObject(href);

  if (!url || !href) {
    return {
      as: fallback || 'span',
      href: '',
    } as const;
  }

  const firstChar = href.at(0);
  const isInternal = isInternalLink(url);
  const isInternalNotDocs = isInternal && !url.pathname.startsWith('/docs');
  const isPageHash = firstChar === '#';
  const rawLink = isInternal ? url.href.split(url.host)[1] : href;

  // Ensure trailing slash for internal pages (consistent with next.config.js trailingSlash: true)
  const addSlash = (path: string) => {
    if (!path || path.startsWith('#')) {
      return path;
    }
    const [pathname, ...rest] = path.split(/(?=[?#])/);

    return pathname.endsWith('/') ? path : `${pathname}/${rest.join('')}`;
  };

  const link = isInternalNotDocs ? addSlash(rawLink) : rawLink;

  return {
    as: isInternalNotDocs ? Link : 'a',
    rel: isInternal ? '' : 'noreferrer noopener',
    target: isInternal ? '' : '_blank',
    href: isPageHash ? url.hash : link,
  } as const;
};

export default parseUrl;
